import { useEffect, useMemo } from "react";

import {
  getDefaultPortalSimListParams,
  portalSimListInfiniteQueryOptions,
} from "@/hooks/usePortalSimListQuery.ts";
import { useGetSimListInfinite } from "@/services/api.ts";
import { hasPendingService } from "@/utils/deviceUtils.tsx";

/**
 * True when any SIM still has `servicePending` (e.g. Flat Mobile ordered but
 * waiting on ID check / activation). Used to hide the FWB "Book a Mobile Abo"
 * cockpit todo — the customer already has a pending mobile subscription.
 *
 * Returns `undefined` while the answer isn't known yet (still loading, or
 * more pages remain to check) — callers must treat that as "don't know yet",
 * not as "false", or the todo will flash on then off once the real answer
 * (a later page holding a pending SIM) comes in.
 */
export const useHasPendingMobileService = (): boolean | undefined => {
  const {
    data: simList,
    isSuccess,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetSimListInfinite(getDefaultPortalSimListParams(), {
    query: portalSimListInfiniteQueryOptions,
  });

  // Evaluating all SIMs requires every page - fetch onward until there's no
  // next page left, same as any other "load everything" infinite query.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return useMemo(() => {
    const sims = simList?.pages?.flatMap((page) => page?.simCards ?? []) ?? [];

    if (sims.some((sim) => hasPendingService(sim))) {
      return true;
    }

    // Not a definitive "no" until every page has been checked.
    if (!isSuccess || hasNextPage) {
      return undefined;
    }

    return false;
  }, [simList, isSuccess, hasNextPage]);
};
